import { IPromise, IScope } from "angular";
import { buildAssigneesSearchRequests, searchItemsToAssignees } from "@gtmhub/assignees/utils/search-utils";
import { IStateInit } from "@gtmhub/core/routing";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { IIndicator } from "@gtmhub/error-handling";
import { OkrMethodologyFacade } from "@gtmhub/goals/components/facades/okr-methodology.facade";
import { SearchService } from "@gtmhub/search/services/search.service";
import { Paginator } from "@gtmhub/teams";
import { Assignee, AssigneeSelectorType } from "@webapp/assignees/models/assignee.models";
import { ISearchParams, SearchOperatorsEnum } from "@webapp/search/models/search-api.models";
import { Search } from "@webapp/search/models/search.models";
import { ISessionSettings } from "@webapp/sessions/models/sessions.model";

interface IBulkAssignParams {
  settings: ISessionSettings;
}

interface IBulkAssignScope extends IScope {
  assignees: Assignee[];
  paginator: Paginator;
  indicators: {
    loading?: IIndicator;
    search?: IIndicator;
  };
  querySearch: string;
  onPageChange(page: number): void;
  searchAssignees(querySearch: string): void;
}

export class BulkAssignCtrl implements IStateInit {
  private assignPermissions: AssigneeSelectorType;

  public static $inject = ["$scope", "SearchService", "$rootScope", "$stateParams", "OkrMethodologyFacade"];

  constructor(
    private $scope: IBulkAssignScope,
    private searchService: SearchService,
    private $rootScope: ITraceRootScopeService,
    private $stateParams: IBulkAssignParams,
    private okrMethodologyFacade: OkrMethodologyFacade
  ) {}

  public stateInit(): IPromise<unknown> {
    return this.init();
  }

  private init = (): IPromise<unknown> => {
    this.$scope.indicators = {};
    this.$scope.onPageChange = this.onPageChange;
    this.$scope.searchAssignees = this.searchAssignees;

    this.setDefaultPaginator();
    this.setAssignPermissions();

    return this.getAssignees(this.$scope.querySearch);
  };

  private setAssignPermissions(): void {
    const settings = this.$stateParams.settings;
    const assignGoalPermissions = this.okrMethodologyFacade.getAssignGoalPermissionsSetting(settings);
    const assignMetricPermissions = this.okrMethodologyFacade.getAssignMetricPermissions(settings);
    this.assignPermissions =
      assignGoalPermissions === "both" || assignMetricPermissions == "both" || assignGoalPermissions !== assignMetricPermissions ? "both" : assignGoalPermissions;
  }

  private searchAssignees = (querySearch: string) => {
    this.$rootScope.traceAction("search_assignees", () => {
      this.$scope.querySearch = querySearch;

      this.setDefaultPaginator();
      this.getAssignees(this.$scope.querySearch, "search");
    });
  };

  private setDefaultPaginator(): void {
    this.$scope.paginator = {
      currentPage: 1,
      itemsPerPage: 15,
      paginatorLimit: 1,
    };
  }

  private onPageChange = (page: number): void => {
    this.$rootScope.traceAction("page_changed", () => {
      this.$scope.paginator.currentPage = page;
      this.getAssignees(this.$scope.querySearch, "search");
    });
  };

  private getAssignees = (searchTerm: string, indicator = "loading"): IPromise<unknown> => {
    this.$scope.indicators[indicator] = { progress: true };

    const searchBody = {
      searchTerm,
      operator: searchTerm ? SearchOperatorsEnum.matchPrefix : SearchOperatorsEnum.matchAny,
      searchRequests: buildAssigneesSearchRequests(this.assignPermissions, searchTerm),
    };
    const queryParams: ISearchParams = {
      skip: (this.$scope.paginator.currentPage - 1) * this.$scope.paginator.itemsPerPage,
      take: this.$scope.paginator.itemsPerPage,
    };

    return this.searchService.getSomeSearchData<Search<"teams"> | Search<"users">>(searchBody, queryParams).then(
      (response) => {
        delete this.$scope.indicators[indicator];

        this.$scope.assignees = searchItemsToAssignees(response.items);

        this.$scope.paginator.totalPages = Math.ceil(response.totalCount / this.$scope.paginator.itemsPerPage);
      },
      (error) => (this.$scope.indicators[indicator] = { error })
    );
  };
}
